import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_TRIES = 12;

const Shell = ({ children }) => (
  <div className="min-h-screen bg-royal-deep sidebar-grain flex items-center justify-center p-6">
    <div className="w-full max-w-md rounded-xl bg-white p-8 text-center rise">{children}</div>
  </div>
);

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const [kind, setKind] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.kind) setKind(data.kind);
        if (data.payment_status === "paid") return setState("paid");
        if (["failed", "expired"].includes(data.payment_status)) return setState("failed");
      } catch {
        /* keep polling — the record may not be visible yet */
      }
      tries += 1;
      if (tries >= MAX_TRIES) return setState("timeout");
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  const trial = kind === "trial_subscription";

  return (
    <Shell>
      {state === "checking" && (
        <div data-testid="payment-checking">
          <Loader2 className="h-8 w-8 animate-spin text-royal mx-auto" />
          <h1 className="font-display text-2xl font-bold text-royal-deep mt-5">Confirming your payment…</h1>
          <p className="text-sm text-muted-foreground mt-2">This only takes a moment. Please don't close this page.</p>
        </div>
      )}
      {state === "paid" && (
        <div data-testid="payment-success">
          <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto" />
          <h1 className="font-display text-3xl font-bold text-royal-deep mt-5">
            {trial ? "You're all set" : "Payment received"}
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            {trial
              ? "Your card is on file and nothing was charged. Your 14-day trial starts now — every feature is unlocked."
              : "Thank you — your invoice has been updated and a receipt is on its way."}
          </p>
        </div>
      )}
      {(state === "failed" || state === "error") && (
        <div data-testid="payment-failed">
          <XCircle className="h-10 w-10 text-red-600 mx-auto" />
          <h1 className="font-display text-2xl font-bold text-royal-deep mt-5">Payment didn't go through</h1>
          <p className="text-sm text-muted-foreground mt-2">Nothing was charged. You can try again from the invoice.</p>
        </div>
      )}
      {state === "timeout" && (
        <div data-testid="payment-timeout">
          <Loader2 className="h-8 w-8 text-gold mx-auto" />
          <h1 className="font-display text-2xl font-bold text-royal-deep mt-5">Still processing</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Your bank is taking a little longer. Check the invoice in a few minutes — it will update itself.
          </p>
        </div>
      )}
      <Link
        to={trial ? "/dashboard" : "/invoices"}
        data-testid="back-to-invoices"
        onClick={() => {
          if (trial) window.location.href = "/dashboard";
        }}
        className="mt-7 inline-flex items-center gap-2 rounded-md bg-royal px-5 min-h-[44px] text-sm font-semibold text-white hover:bg-royal-deep transition-colors duration-200"
      >
        {trial ? "Go to my dashboard" : "Back to invoices"} <ArrowRight className="h-4 w-4" />
      </Link>
    </Shell>
  );
}

export function PaymentCancel() {
  return (
    <Shell>
      <div data-testid="payment-cancelled">
        <XCircle className="h-10 w-10 text-muted-foreground mx-auto" />
        <h1 className="font-display text-2xl font-bold text-royal-deep mt-5">Checkout cancelled</h1>
        <p className="text-sm text-muted-foreground mt-2">No charge was made. The invoice is still waiting for you.</p>
      </div>
      <Link
        to="/invoices"
        data-testid="back-to-invoices"
        className="mt-7 inline-flex items-center gap-2 rounded-md bg-royal px-5 min-h-[44px] text-sm font-semibold text-white hover:bg-royal-deep transition-colors duration-200"
      >
        Back to invoices <ArrowRight className="h-4 w-4" />
      </Link>
    </Shell>
  );
}
